import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type {
    AnalyticsCumulatedSalesItem,
    ChannelConsolidatedSales,
    ChannelConsolidatedSalesStoreItem,
} from "helpers/parsers/parseConsolidatedSales";
import {
    parseConsolidatedSales,
    parseConsolidatedSalesTotal,
    parseTopPerformingTitle,
} from "helpers/parsers/parseConsolidatedSales";
import { formatDateToIso, mergeDeep, translationsWithFallback } from "helpers/utils";
import { toast } from "react-toastify";
import type { AnalyticsFilters, IDatesRange } from "types";

import type { ChannelKind, TFetchStatus } from "./types";

const sanitizeDate = (date) => {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
        throw new Error("Invalid date");
    }

    return parsedDate.toLocaleDateString("en-CA");
};

// Indicators only read the metrics and the top block only the top performers: each widget fetches its own part
export const fetchDashboardData = createAsyncThunk(
    "analytics/fetchDashboardData",
    async (
        { dates, type }: { dates: IDatesRange; type: "indicators" | "top-performing-data" },
        { rejectWithValue },
    ) => {
        // Calendar days, as the other analytics calls: a range of whole months is served from monthly aggregates
        const params = {
            dates: {
                startDate: dates.startDate ? sanitizeDate(dates.startDate) : undefined,
                endDate: dates.endDate ? sanitizeDate(dates.endDate) : undefined,
            },
        };
        try {
            if (type === "indicators") {
                const periodMetricsResponse = await axios.get(
                    import.meta.env.REACT_APP_API_ENDPOINT + `/analytics/periods/metrics`,
                    { params },
                );

                return { data: periodMetricsResponse.data.data as Pick<DashboardData, "metrics">, type };
            }

            const topPerformersResponse = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/analytics/top-performers`,
                { params },
            );

            const topPerformers = topPerformersResponse.data.data as {
                topPerformingTitles: AnalyticsCumulatedSalesItem[];
                topPerformingBeneficiaries: TopPerformingData[];
                mostPerformantChannels: TopPerformingData[];
            };

            const topPerformingTitles = topPerformers.topPerformingTitles.map((o) => parseTopPerformingTitle(o));

            return {
                data: { ...topPerformers, topPerformingTitles } as Partial<DashboardData>,
                type,
            };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

const formatAnalyticsFilters = (filters: AnalyticsFilters) => {
    const { dates, ...rest } = filters;

    const queryObj: Record<string, string | number | (string | number)[] | boolean | Date> = {
        ...rest,
        ...(dates || {}),
    };

    return queryObj;
};

const getSortColumnName = (sortProperty: string) => {
    switch (sortProperty) {
        case "title":
            return "name";
        case "rev":
            return "turnoverPostDistributor";
        case "ca-ppht":
            return "turnover";
        case "qty-returned":
            return "quantitiesReturned";
        case "qty-net":
            return "quantities";

        case "cumulated-rev":
            return "cumulatedTurnoverPostDistributor";
        case "cumulated-ca-ppht":
            return "cumulatedTurnover";
        case "cumulated-qty-returned":
            return "cumulatedQuantitiesReturned";
        case "cumulated-qty-net":
            return "cumulatedQuantities";
        default:
            return sortProperty;
    }
};

type FetchChannelsSalesParams = {
    dates: IDatesRange;
    titleId: number;
    type: AnalyticsChartType;
};

export const fetchAnalyticsChannelsSales = createAsyncThunk(
    "analytics/fetchAnalyticsChannelsSales",
    async ({ titleId, dates, type }: FetchChannelsSalesParams, { rejectWithValue }) => {
        const params = formatAnalyticsFilters({ titleIds: [titleId], dates });
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/analytics/channels/title/sales`,
                {
                    params: {
                        ...params,
                        startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                        endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    },
                },
            );
            return { data: response.data.data, type };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAnalyticsSales = createAsyncThunk(
    "analytics/fetchAnalyticsSales",
    async ({ params, type }: { params: FetchSalesParams; type: AnalyticsChartType }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/sales", { params });
            return { data: response.data.data, type };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Dedicated endpoint: /analytics/sales returned every sale grouped per title x month only to read its countries
export const fetchAnalyticsCountries = createAsyncThunk(
    "analytics/fetchAnalyticsCountries",
    async ({ dates }: { dates: IDatesRange }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/countries", {
                params: {
                    startDate: dates.startDate ? sanitizeDate(dates.startDate) : undefined,
                    endDate: dates.endDate ? sanitizeDate(dates.endDate) : undefined,
                },
            });
            return response.data.data as string[];
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const refreshSalesAnalytics = createAsyncThunk("analytics/refresh-sales", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/refresh-sales");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchAnalyticsCachedSales = createAsyncThunk(
    "analytics/cached-sales",
    async (
        {
            storeKey,
            withTotal,
            groupMethods,
            ...filters
        }: {
            storeKey: string;
            withTotal?: boolean;
            groupMethods?: GroupMethod[];
        } & AnalyticsFilters,
        { rejectWithValue },
    ) => {
        try {
            const params = formatAnalyticsFilters(filters);
            const page = filters.page || 1;

            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/consolidated-sales", {
                params: {
                    ...params,
                    startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                    endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    page,
                    withTotal,
                    groupMethods,
                    fromCache: true,
                },
            });

            const results = response.data.data;
            const isDone = !results?.length || !params.limit || results.length < params.limit;

            return {
                [storeKey]: {
                    status: { isDone, page },
                    pages: {
                        [page]: results,
                    },
                },
            };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const generateAnalyticsConsolidatedSalesReport = createAsyncThunk(
    "analytics/consolidated-sales-report",
    async (filters: AnalyticsFilters, { rejectWithValue }) => {
        try {
            const params = formatAnalyticsFilters(filters);

            if (params.sortColumn) {
                params.sortColumn = getSortColumnName(`${params.sortColumn}`);
                params.channelIds = [];
            }

            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/consolidated-sales/report",
                {
                    params: {
                        ...params,
                        startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                        endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    },
                },
            );

            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data);
        }
    },
);

/**
 * Total of one consolidated sales table ("all": the all-channels one, which also brings the others).
 * `byChannel` null when the server cannot give totals: each table then gets its total with its rows.
 */
export const fetchAnalyticsConsolidatedTotals = createAsyncThunk(
    "analytics/consolidated-sales-totals",
    async (
        {
            key,
            channelId,
            dates,
            titleIds,
        }: { key: string; channelId: string } & Pick<AnalyticsFilters, "dates" | "titleIds">,
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/consolidated-sales/totals",
                {
                    params: {
                        channelIds: [channelId],
                        startDate: dates?.startDate ? sanitizeDate(dates.startDate) : undefined,
                        endDate: dates?.endDate ? sanitizeDate(dates.endDate) : undefined,
                        titleIds: titleIds?.length ? titleIds : undefined,
                    },
                },
            );
            const rows: ChannelConsolidatedSales[] | null = response.data.data;

            return {
                key,
                byChannel: rows
                    ? Object.fromEntries(rows.map((row) => [String(row.channelId), parseConsolidatedSalesTotal(row)]))
                    : null,
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

/**
 * Each page recomputes the whole channel: few large pages (server cap: 1000) instead of dozens of
 * 20-row ones, which took minutes for a big channel and held up the tables below it.
 */
const CONSOLIDATED_SALES_PAGE_SIZE = 500;

export const fetchAnalyticsConsolidatedSales = createAsyncThunk(
    "analytics/consolidated-sales",
    async (
        {
            withTotal = false,
            fromCache = false,
            storeKey,
            groupMethods,
            ...filters
        }: {
            withTotal?: boolean;
            fromCache?: boolean;
            storeKey: string;
            groupMethods?: GroupMethod[];
        } & AnalyticsFilters,
        { rejectWithValue },
    ) => {
        try {
            const page = filters.page || 1;
            const channelId = filters.channelIds?.[0] || "all";
            const _filters: AnalyticsFilters = {
                ...filters,
                page,
                limit: CONSOLIDATED_SALES_PAGE_SIZE,
                channelIds: [channelId],
            };

            const params = formatAnalyticsFilters(_filters);

            if (params.sortColumn) {
                params.sortColumn = getSortColumnName(`${params.sortColumn}`);
            }

            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/consolidated-sales", {
                params: {
                    ...params,
                    startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                    endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    groupMethods,
                    fromCache,
                    withTotal,
                },
            });

            const results = response.data.data;
            const isDone = !results?.length || !params.limit || results.length < params.limit;

            const rowOfTotal = withTotal && results[0] ? parseConsolidatedSalesTotal(results[0]) : undefined;

            const storeItem = {
                status: { page, isDone },
                pages: { [page]: results.map((o) => parseConsolidatedSales(o)) },
                channelId,
            };

            if (rowOfTotal) {
                storeItem["total"] = rowOfTotal;
            }

            return {
                [storeKey]: storeItem,
            };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAnalyticsChannelsPeriodSales = createAsyncThunk(
    "analytics/channels-period-sales",
    async (filters: AnalyticsFilters, { rejectWithValue }) => {
        try {
            const params = formatAnalyticsFilters(filters);

            const storeKey = getStoreKeyFromFilters(filters);

            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/channels/periods/sales",
                {
                    params: {
                        ...params,
                        startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                        endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    },
                },
            );

            const results = response.data.data;

            return {
                [storeKey]: results,
            };
        } catch (error) {
            /* empty */
        }
    },
);

export type GroupMethod =
    | "perYear"
    | "perMonth"
    | "perDate"
    | "perChannel"
    | "perTitle"
    | "perCountry"
    | "dontGroup"
    | "groupAll";

export type GroupedChannelConsolidatedSales = ChannelConsolidatedSales & {
    total_turnover?: number;
    total_channel_kind_turnover?: number;
    total_channel_id_turnover?: number;
    total_turnover_1?: number;
    total_channel_kind_turnover_1?: number;
    total_channel_id_turnover_1?: number;
    total_turnover_2?: number;
    total_channel_kind_turnover_2?: number;
    total_channel_id_turnover_2?: number;
    total_quantities?: number;
    total_channel_kind_quantities?: number;
    total_channel_id_quantities?: number;
};

export type ChannelPeriodSalesFromAPI = {
    salesN: GroupedChannelConsolidatedSales[];
    salesN_1: GroupedChannelConsolidatedSales[];
};

export type AnalyticsChartType =
    | "line-chart"
    | "pie-chart-per-channel"
    | "pie-chart-per-country"
    | "bar-chart"
    | "indicators"
    | "top-performing-data"
    | "channels-metrics"
    | "consolidated-sales";

export type FetchSalesParams = {
    startDate: Date;
    endDate: Date;
    groupMethod: GroupMethod;
    titleIds?: number[];
    channelIds?: number[];
    countryCodes?: number[];
};

export type TopPerformingData = {
    id: number;
    titleId?: number;
    type?: number;
    kind?: number;
    name: string;
    amount: number | string;
    avatarUrl?: string;
    author?: string;
};

export interface DashboardData {
    topPerformingTitles: TopPerformingData[];
    topPerformingBeneficiaries: TopPerformingData[];
    mostPerformantChannels: TopPerformingData[];
    metrics: {
        nbTitles: { N: number; N_1: number };
        avgSales: { N: number; N_1: number };
        avgRoyalties: { N: number; N_1: number };
        totalSales: { N: number; N_1: number };
        totalRoyalties: { N: number; N_1: number };
    };
}

type ChannelSalesPeriodValues = { N: number | null; N_1: number | null; growthIndex: number | null };

export type ChannelSalesData = {
    quantities: ChannelSalesPeriodValues;
    turnover: ChannelSalesPeriodValues;
    turnover_1: ChannelSalesPeriodValues;
    turnover_2: ChannelSalesPeriodValues;
};

export type ChannelConsolidatedSalesData = {
    total_channel_id_quantities: number;
    total_channel_id_turnover: number;
    total_channel_id_turnover_1?: number;
    total_channel_id_turnover_2?: number;
};

export type ChannelSales = {
    id: number;
    kind: ChannelKind;
    name: string;
    data: ChannelSalesData | ChannelConsolidatedSalesData;
};

export type TConsolidatedChannelSales = {
    titleId: number;
    avatarUrl: string;
    name: string;
    ean13: string;
    date: string;
    saleYear: number;
    saleMonth: number;
    channelId: number;
    channelName: string;
    channelKind: number;
    periodId: number;
    countryMarket: string;
    optimalPrice: number;
    quantities: number;
    quantitiesReturned: number;
    turnover: number;
    turnoverPostDistributor: number;
    companyId: number;
    saleId: number;
    optimalPriceCurrency: string;
    distributorPercentage: null;
    total_turnover: number;
    total_channel_kind_turnover: number;
    total_channel_id_turnover: number;
    total_quantities: number;
    total_channel_kind_quantities: number;
    total_channel_id_quantities: number;
};

export interface TitleSales {
    id: number;
    channelId: number;
    channelName: string;
    countryMarket: string;
    date: string;
    ean13: string;
    name: string;
    optimalPrice: number;
    quantities: number;
    titleId: number;
    turnover: number;
    turnover_1?: number | null;
    turnover_2?: number | null;
}

interface AnalyticsDashboard {
    indicators: DashboardData | undefined;
    "top-performing-data": DashboardData | undefined;
}

export interface AnalyticsChannelsSales {
    "channels-metrics": { data: ChannelSales[] | TConsolidatedChannelSales[]; titleIds: string[] };
}

export interface AnalyticsSales {
    "line-chart": TitleSales[];
    "pie-chart-per-channel": TitleSales[];
    "pie-chart-per-country": TitleSales[];
    "bar-chart": TitleSales[];
}

export type AnalyticsSalesStatus = Record<string, TFetchStatus>;

export type AnalyticsConsolidatedSalesStatus = Record<string, TFetchStatus>;

interface AnalyticsState {
    dashboard: DashboardData | undefined;
    dashboardStatus: AnalyticsSalesStatus;
    analyticsSales: AnalyticsSales;
    analyticsCountries: string[];
    analyticsDashboard: AnalyticsDashboard;
    analyticsChannelsSales: AnalyticsChannelsSales;

    analyticsSalesRefresh: Date | undefined;

    analyticsConsolidatedSales: Record<
        string,
        {
            status: { isDone: boolean; page: number };
            pages: Record<string, ChannelConsolidatedSalesStoreItem[]>;
            channelId: string;
            total?: ChannelConsolidatedSalesStoreItem;
        }
    >;

    analyticsCachedSales: Record<
        string,
        {
            status: { isDone: boolean; page: number };
            pages: Record<string, AnalyticsCumulatedSalesItem[]>;
        }
    >;
    analyticsCachedSalesStatus: Record<string, TFetchStatus>;

    analyticsChannelsPeriodSales: Record<string, ChannelPeriodSalesFromAPI>;
    analyticsChannelsPeriodSalesStatus: TFetchStatus;

    channelsSalesStatus: TFetchStatus;
    analyticsSalesStatus: AnalyticsSalesStatus;

    /**
     * GET analytics table status
     */
    refreshAnalyticsStatus: TFetchStatus;
    analyticsCountriesStatus: TFetchStatus;

    analyticsSalesRefreshStatus: TFetchStatus;
    /** Refresh job the page waits for: kept here so it is still followed after leaving the page */
    followedRefreshJobId: number | null;
    analyticsConsolidatedSalesStatus: AnalyticsConsolidatedSalesStatus;
    /** Totals of the consolidated sales tables for the filters of `key`, loaded table by table */
    analyticsConsolidatedTotals: {
        key: string | null;
        // The server cannot give totals (company never refreshed, period filter)
        unavailable: boolean;
        byChannel: Record<string, ChannelConsolidatedSalesStoreItem>;
        status: Record<string, TFetchStatus>;
    };

    analyticsConsolidatedSalesReport: any;
    analyticsConsolidatedSalesReportStatus: TFetchStatus;
}

const initialAnalyticsSales = {
    "line-chart": [],
    "pie-chart-per-channel": [],
    "pie-chart-per-country": [],
    "bar-chart": [],
};

const initialAnalyticsSalesStatus: AnalyticsSalesStatus = {
    "line-chart": "idle",
    "pie-chart-per-channel": "idle",
    "pie-chart-per-country": "idle",
    "bar-chart": "idle",
};

const initialState: AnalyticsState = {
    dashboard: undefined,
    dashboardStatus: { indicators: "idle", "top-performing-data": "idle" },
    analyticsSales: initialAnalyticsSales,
    analyticsCountries: [],
    analyticsDashboard: {
        indicators: undefined,
        "top-performing-data": undefined,
    },
    analyticsChannelsSales: { "channels-metrics": { data: [], titleIds: [] } }, // If there is only channelsMetrics here, do we need this? Couldn't we simply pass the data wihthout the type?
    analyticsConsolidatedSales: {},

    analyticsChannelsPeriodSales: {},
    analyticsChannelsPeriodSalesStatus: "idle",

    channelsSalesStatus: "idle",
    analyticsSalesStatus: initialAnalyticsSalesStatus,
    refreshAnalyticsStatus: "idle",
    analyticsCountriesStatus: "idle",
    analyticsConsolidatedSalesStatus: {},
    analyticsConsolidatedTotals: { key: null, unavailable: false, byChannel: {}, status: {} },

    analyticsSalesRefresh: undefined,
    analyticsSalesRefreshStatus: "idle",
    followedRefreshJobId: null,

    analyticsCachedSales: {},
    analyticsCachedSalesStatus: {},

    analyticsConsolidatedSalesReport: {},
    analyticsConsolidatedSalesReportStatus: "idle",
};

// Function to generate a unique key for the store based on filters
// This is used to cache the results of the API call
export const getStoreKeyFromFilters = (
    filters: AnalyticsFilters,
    groupMethods?: GroupMethod[],
    fromCache?: boolean,
) => {
    const startDateStr = filters.dates?.startDate ? formatDateToIso(filters.dates.startDate) : null;
    const endDateStr = filters.dates?.endDate ? formatDateToIso(filters.dates.endDate) : null;

    return [
        ...(filters.channelIds || []).map((o) => `ch-${o}`),
        ...(filters.periodIds || []).map((o) => `p-${o}`),
        ...(filters.titleIds || []).map((o) => `t-${o}`),
        ...(groupMethods || []).map((o) => `gr-${o}`),
        ...(filters.countryMarkets || []).map((o) => `co-${o}`),
        startDateStr || "",
        endDateStr || "",
        filters.sortColumn || "",
        filters.sortDirection || "",
        `fromCache-${!!fromCache}`,
    ]
        .sort() // Sort to ensure the same filters always generate the same key
        .join("");
};

export const analyticsSlice = createSlice({
    name: "analytics",
    initialState,
    reducers: {
        resetAllGraphData(state) {
            // Reset fetch status
            state.channelsSalesStatus = "idle";
            state.analyticsSalesStatus = initialAnalyticsSalesStatus;
            state.dashboardStatus = { indicators: "idle", "top-performing-data": "idle" };
            state.analyticsConsolidatedSalesStatus = {};

            // Reset data
            state.analyticsSales = initialAnalyticsSales;
            state.analyticsChannelsSales = { "channels-metrics": { data: [], titleIds: [] } };
            state.analyticsDashboard = {
                indicators: undefined,
                "top-performing-data": undefined,
            };
            state.analyticsConsolidatedSales = {};

            state.analyticsSalesRefresh = undefined;
            state.analyticsSalesRefreshStatus = "idle";
        },

        setRefreshAnalyticsStatus(state, action) {
            state.refreshAnalyticsStatus = action.payload;
        },

        followRefreshJob(state, { payload }: PayloadAction<number>) {
            state.followedRefreshJobId = payload;
        },

        /** The followed refresh job ended: on success both tabs reload, consolidated sales drop their stale pages */
        analyticsRefreshEnded(state, { payload: succeeded }: PayloadAction<boolean>) {
            state.followedRefreshJobId = null;
            if (!succeeded) return;
            state.analyticsSalesRefresh = new Date();
            // A refresh can bring sales in new countries: the country filter reloads its list
            state.analyticsCountriesStatus = "idle";
            state.analyticsConsolidatedSales = {};
            state.analyticsConsolidatedSalesStatus = {};
            state.analyticsConsolidatedTotals = initialState.analyticsConsolidatedTotals;
        },

        resetAnalyticsConsolidatedSales(state) {
            state.analyticsConsolidatedSales = {};
            state.analyticsConsolidatedSalesStatus = {};
            state.analyticsConsolidatedTotals = initialState.analyticsConsolidatedTotals;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchDashboardData.fulfilled, (state, action) => {
            const { data, type } = action.payload;
            // Each widget brings its half (metrics or top performers): keep the other one
            state.dashboard = { ...state.dashboard, ...data } as DashboardData;
            state.analyticsDashboard = { ...state.analyticsDashboard, [type]: data as DashboardData };
            state.dashboardStatus = { ...state.dashboardStatus, [type]: "succeeded" };
        });
        builder.addCase(fetchDashboardData.rejected, (state, { payload, meta }) => {
            const { type } = meta.arg;
            state.dashboardStatus = { ...state.dashboardStatus, [type]: "failed" };
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDashboardData.pending, (state, action) => {
            const { type } = action.meta.arg;
            state.dashboardStatus = { ...state.dashboardStatus, [type]: "pending" };
        });
        builder.addCase(fetchAnalyticsChannelsSales.fulfilled, (state, action) => {
            state.channelsSalesStatus = "succeeded";
            const { data, type } = action.payload;
            state.analyticsChannelsSales = {
                ...state.analyticsChannelsSales,
                [type]: { data: data.salesN, titleIds: data.salesN.map((sale) => sale.titleId) },
            };
        });
        builder.addCase(fetchAnalyticsChannelsSales.pending, (state) => {
            state.channelsSalesStatus = "pending";
        });
        builder.addCase(fetchAnalyticsChannelsSales.rejected, (state, { payload }) => {
            state.channelsSalesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAnalyticsSales.pending, (state, action) => {
            const { type } = action.meta.arg;
            state.analyticsSalesStatus = { ...state.analyticsSalesStatus, [type]: "pending" };
        });
        builder.addCase(fetchAnalyticsSales.fulfilled, (state, action) => {
            const { data: titleSalesData, type } = action.payload;
            state.analyticsSales = { ...state.analyticsSales, [type]: titleSalesData };
            state.analyticsSalesStatus = { ...state.analyticsSalesStatus, [type]: "succeeded" };
        });
        builder.addCase(fetchAnalyticsSales.rejected, (state, { payload, meta }) => {
            const { type } = meta.arg;
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsSalesStatus = { ...state.analyticsSalesStatus, [type]: "failed" };
        });

        builder.addCase(fetchAnalyticsCountries.pending, (state, action) => {
            state.analyticsCountriesStatus = "pending";
        });
        builder.addCase(fetchAnalyticsCountries.fulfilled, (state, action) => {
            state.analyticsCountries = action.payload;
            state.analyticsCountriesStatus = "succeeded";
        });
        builder.addCase(fetchAnalyticsCountries.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsCountriesStatus = "rejected";
        });

        builder.addCase(refreshSalesAnalytics.pending, (state, action) => {
            state.analyticsSalesRefreshStatus = "pending";
        });
        // The refresh only started: its job is followed until it ends (see analyticsRefreshEnded)
        builder.addCase(refreshSalesAnalytics.fulfilled, (state, { payload }) => {
            state.analyticsSalesRefreshStatus = "succeeded";
            // Followed from the start: a short refresh may end before any poll sees it running
            state.followedRefreshJobId = Number(payload);
        });
        builder.addCase(refreshSalesAnalytics.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsSalesRefreshStatus = "rejected";
        });

        builder.addCase(fetchAnalyticsConsolidatedTotals.pending, (state, { meta }) => {
            // New filters: the totals of the previous ones no longer apply
            if (state.analyticsConsolidatedTotals.key !== meta.arg.key) {
                state.analyticsConsolidatedTotals = {
                    key: meta.arg.key,
                    unavailable: false,
                    byChannel: {},
                    status: {},
                };
            }
            state.analyticsConsolidatedTotals.status[meta.arg.channelId] = "pending";
        });
        builder.addCase(fetchAnalyticsConsolidatedTotals.fulfilled, (state, { payload, meta }) => {
            const totals = state.analyticsConsolidatedTotals;
            // An answer for filters changed meanwhile is dropped
            if (payload.key !== totals.key) return;
            if (!payload.byChannel) {
                totals.unavailable = true;
            } else {
                // A channel absent from the answer has no sales in the range: its total is empty
                Object.assign(totals.byChannel, payload.byChannel);
            }
            totals.status[meta.arg.channelId] = "succeeded";
        });
        builder.addCase(fetchAnalyticsConsolidatedTotals.rejected, (state, { meta }) => {
            // No toast: this table then loads its total with its rows, as before
            if (meta.arg.key !== state.analyticsConsolidatedTotals.key) return;
            state.analyticsConsolidatedTotals.status[meta.arg.channelId] = "rejected";
        });

        builder.addCase(fetchAnalyticsConsolidatedSales.pending, (state, action) => {
            state.analyticsConsolidatedSalesStatus = mergeDeep(state.analyticsConsolidatedSalesStatus, {
                [action.meta.arg.storeKey]: "pending",
            });
        });
        builder.addCase(fetchAnalyticsConsolidatedSales.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsConsolidatedSales = mergeDeep(state.analyticsConsolidatedSales, data);
            state.analyticsConsolidatedSalesStatus = mergeDeep(state.analyticsConsolidatedSalesStatus, {
                [action.meta.arg.storeKey]: "succeeded",
            });
        });
        builder.addCase(fetchAnalyticsConsolidatedSales.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsConsolidatedSalesStatus = mergeDeep(state.analyticsConsolidatedSalesStatus, {
                [meta.arg.storeKey]: "failed",
            });
        });

        builder.addCase(generateAnalyticsConsolidatedSalesReport.pending, (state, action) => {
            state.analyticsConsolidatedSalesReportStatus = "pending";
        });
        builder.addCase(generateAnalyticsConsolidatedSalesReport.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsConsolidatedSalesReport = data;
            state.analyticsConsolidatedSalesReportStatus = "succeeded";
        });
        builder.addCase(generateAnalyticsConsolidatedSalesReport.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsConsolidatedSalesReportStatus = "rejected";
        });

        builder.addCase(fetchAnalyticsCachedSales.pending, (state, action) => {
            state.analyticsCachedSalesStatus = mergeDeep(state.analyticsCachedSalesStatus, {
                [action.meta.arg.storeKey]: "pending",
            });
        });
        builder.addCase(fetchAnalyticsCachedSales.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsCachedSales = mergeDeep(state.analyticsCachedSales, data);
            state.analyticsCachedSalesStatus = mergeDeep(state.analyticsCachedSalesStatus, {
                [action.meta.arg.storeKey]: "succeeded",
            });
        });
        builder.addCase(fetchAnalyticsCachedSales.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsCachedSalesStatus = mergeDeep(state.analyticsCachedSalesStatus, {
                [meta.arg.storeKey]: "rejected",
            });
        });

        builder.addCase(fetchAnalyticsChannelsPeriodSales.pending, (state, action) => {
            state.analyticsChannelsPeriodSalesStatus = "pending";
        });
        builder.addCase(fetchAnalyticsChannelsPeriodSales.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsChannelsPeriodSales = mergeDeep(state.analyticsChannelsPeriodSales, data);
            state.analyticsChannelsPeriodSalesStatus = "succeeded";
        });
        builder.addCase(fetchAnalyticsChannelsPeriodSales.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsChannelsPeriodSalesStatus = "rejected";
        });
    },
});

export const {
    resetAllGraphData,
    setRefreshAnalyticsStatus,
    resetAnalyticsConsolidatedSales,
    followRefreshJob,
    analyticsRefreshEnded,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
